import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
  Tab,
  Tabs,
} from '@mui/material';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import PictureAsPdfRounded from '@mui/icons-material/PictureAsPdfRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import RadioButtonUncheckedRounded from '@mui/icons-material/RadioButtonUncheckedRounded';
import { useTranslation } from 'react-i18next';
import { buildCapDossierCover } from '@dosfilos/domain';
import type {
  CapAssistedAnalysisProps,
  CapNetworkSubmission,
  ReviewerDecision,
} from '@dosfilos/domain';
import { networkApi } from '../../lib/api/network.api';
import { useTenantStore } from '../../stores/tenant.store';
import { CapEvidencePanel } from './components/CapEvidencePanel';
import { errorText } from '../../lib/error-text';
import { formatDate } from '../../lib/i18n-format';
import { CapDossierCover } from './components/CapDossierCover';
import { CapAnalysisPanel } from './components/CapAnalysisPanel';
import {
  ConditionalApprovalDialog,
  type ConditionDraft,
} from './components/ConditionalApprovalDialog';
import { CapDossierPdfDocument } from './CapDossierPdfDocument';
import { buildCapDossierPdfData } from './cap-dossier-pdf-data';
import { openGeneratedPdf } from '../../lib/open-generated-pdf';
import { renderWithoutHyphenation } from '../../lib/pdf-hyphenation';

// ─── El panel donde se decide una propuesta CAP ─────────────────────────────
//
// LO QUE SE LEE ACÁ ES EXCLUSIVAMENTE EL PAQUETE CONGELADO. No hay una lectura
// al tenant del centro, ni la puede haber: el caso de uso detrás sostiene un solo
// repositorio. El revisor decide sobre LA EVIDENCIA PRESENTADA, que es la regla
// que ya rige para membresía — y por eso la fecha de ensamblado va arriba, a la
// vista, junto a la auditoría que la respalda.
//
// APROBAR OTORGA LA CERTIFICACIÓN. No es un cambio de estado más: por eso el
// botón lo dice con todas las letras y la confirmación explica la consecuencia
// ANTES, no después. Y por eso `certificationId` en la respuesta se muestra: es
// la prueba de que el acto tuvo el efecto que promete.

/** Los rótulos de las secciones A–K salen del marco, no de la UI: son el índice
 *  normativo del documento que TMAI pide. Traducirlos acá inventaría un
 *  vocabulario paralelo al del marco. */
const SECTION_LABEL: Record<string, string> = {
  A: 'A · Purpose',
  B: 'B · Learning outcomes',
  C: 'C · Entrance requirements',
  D: 'D · Instructional language',
  E: 'E · Core coursework',
  F: 'F · Electives',
  G: 'G · Delivery format',
  H: 'H · Time to complete',
  I: 'I · Faculty',
  J: 'J · Assessment',
  K: 'K · Spiritual health',
  annex_ccds: 'Attachment 2 · Course descriptions',
  annex_matrix: 'Attachment 4 · Alignment matrix',
};

/** En orden de lectura: lo que la malla mide, la evidencia que lo sostiene, y
 *  recién después lo que el centro escribió y firmó. */
type EvidenceTab = 'audit' | 'ccds' | 'matrix' | 'sections' | 'stated';

/** El idioma del análisis asistido. Fijo, no el de la pantalla: ver el bloque
 *  en `generateAnalysis`. */
const ANALYSIS_LOCALE = 'en' as const;

export function CapReviewPage() {
  const { t } = useTranslation('sis');
  const { t: tCap } = useTranslation('cap');
  const navigate = useNavigate();
  const { submissionId } = useParams<{ submissionId: string }>();
  const tenantId = useTenantStore((s) => s.currentTenantId);

  const [row, setRow] = useState<CapNetworkSubmission | null>(null);
  const [observations, setObservations] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [granted, setGranted] = useState<string | null>(null);
  const [conditionsOpen, setConditionsOpen] = useState(false);
  /** El borrador del modelo. `null` mientras no se haya pedido nunca: la
   *  pantalla funciona entera sin él, así que su ausencia no es un error. */
  const [analysis, setAnalysis] = useState<CapAssistedAnalysisProps | null>(null);
  const [analysing, setAnalysing] = useState(false);
  /** La pestaña abierta. Arranca en la auditoría: es lo que la malla MIDE, y es
   *  por donde se empieza a leer un expediente. */
  const [tab, setTab] = useState<EvidenceTab>('audit');

  const load = useCallback(async () => {
    if (!tenantId || !submissionId) return;
    setError('');
    try {
      setRow(await networkApi.getCapSubmissionDetail({ tenantId, submissionId }));
      // El análisis se carga aparte y su fallo NO rompe la pantalla: es una
      // ayuda, no parte del expediente que el revisor vino a leer.
      try {
        const { analyses } = await networkApi.listCapAnalyses({ tenantId, submissionId });
        setAnalysis(analyses[0] ?? null);
      } catch {
        setAnalysis(null);
      }
    } catch (e) {
      setError(errorText(e));
    }
  }, [tenantId, submissionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const generateAnalysis = async () => {
    if (!tenantId || !submissionId || !row) return;
    setAnalysing(true);
    try {
      // EL ANÁLISIS VA EN INGLÉS, siempre, sin importar el idioma de la pantalla.
      // TMAI es un organismo de Estados Unidos y el paquete ya se le somete en
      // inglés (§III.A): un fundamento en español obligaría al revisor a traducir
      // lo único que está ahí para ahorrarle trabajo, y el borrador de
      // observaciones saldría en un idioma distinto al del expediente que
      // acompaña. Mismo criterio que `inLocale()` en `lib/i18n-format.ts`: un
      // documento con su propia audiencia se fija a la lengua de esa audiencia,
      // no a la de quien lo mira.
      //
      // Por eso las etiquetas se piden con `lng: 'en'` explícito y no en el
      // idioma activo.
      const cover = buildCapDossierCover(row.packet);
      setAnalysis(
        await networkApi.generateCapAnalysis({
          tenantId,
          submissionId,
          locale: ANALYSIS_LOCALE,
          areaLabels: tCap('subjectAreas', {
            returnObjects: true,
            lng: ANALYSIS_LOCALE,
          }) as Record<string, string>,
          // El enunciado de cada hallazgo, YA RESUELTO con sus números y su
          // área — la misma frase de la portada. Sin esto el modelo adivina qué
          // significa el código: con `non_academic_unattested` escribió «un curso
          // sin atestación», cuando lo que falta son REQUISITOS no académicos.
          findingStatements: cover.findings.map((f) =>
            tCap(`dossier.findings.${f.code}`, {
              ...f.params,
              lng: ANALYSIS_LOCALE,
              area: f.area ? tCap(`subjectAreas.${f.area}`, { lng: ANALYSIS_LOCALE }) : '',
            }),
          ),
        }),
      );
    } catch (e) {
      setError(errorText(e));
    } finally {
      setAnalysing(false);
    }
  };

  const decide = async (
    decision: ReviewerDecision,
    conditions?: { items: ConditionDraft[]; reviewBy: string },
  ) => {
    if (!tenantId || !submissionId) return;
    setBusy(true);
    setError('');
    try {
      const out = await networkApi.decideCapSubmission({
        tenantId,
        submissionId,
        decision,
        observations,
        ...(conditions ? { conditions } : {}),
      });
      if (out.certificationId) setGranted(out.certificationId);
      setConditionsOpen(false);
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  if (!row) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        {error ? <Alert severity="error">{error}</Alert> : <CircularProgress />}
      </Box>
    );
  }

  /** El expediente impreso, para la mesa del comité. NO es un resumen aparte:
   *  imprime la misma portada que se ve arriba y la misma evidencia de abajo. */
  const printDossier = async (): Promise<void> => {
    setBusy(true);
    try {
      await openGeneratedPdf(async () => {
        const { pdf } = await import('@react-pdf/renderer');
        const data = buildCapDossierPdfData({
          row,
          cover: buildCapDossierCover(row.packet),
          networkName: t('network.dashboard.title', { defaultValue: 'TMAI' }),
          generatedOn: new Date(),
          translators: {
            tc: (key, params) => tCap(key, params ?? {}),
            ts: (key, params) => t(key, params ?? {}),
            formatDate: (v) => formatDate(v, { dateStyle: 'long' }),
          },
        });
        // Las reglas de guionado de @react-pdf son inglesas y parten palabras
        // en español («Pred-icación»). El expediente se reparte impreso.
        return renderWithoutHyphenation(() => pdf(<CapDossierPdfDocument data={data} />).toBlob());
      }, `${row.programName} · ${row.capCode}.pdf`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const open = row.status === 'submitted';
  const sections = row.packet.sections;
  const complete = sections.filter((s) => s.complete).length;
  const hardGaps = sections.filter((s) => s.hardGap).length;
  /** Aprobar exige que NO queden brechas duras. La compuerta vive en el servidor
   *  también; acá se muestra para que el revisor no descubra el rechazo después
   *  de escribir su decisión. */
  const canApprove = open && row.submissionKind === 'formal' && hardGaps === 0;

  return (
    <Box>
      <Button startIcon={<ArrowBackRounded />} onClick={() => navigate('/red')} sx={{ mb: 2 }}>
        {t('network.review.backToInbox')}
      </Button>

      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {row.programName}
        </Typography>
        <Chip size="small" label={row.capCode} color="primary" />
        <Chip size="small" variant="outlined" label={t(`network.inbox.cap.level.${row.level}`)} />
        {row.submissionKind === 'advisory' && (
          <Chip size="small" label={t('network.inbox.cap.advisory')} />
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {row.seminaryName}
        {row.tmaiSiteAbbreviation ? ` · ${row.tmaiSiteAbbreviation}` : ''} ·{' '}
        {t('network.inbox.cap.round', { round: row.round })}
      </Typography>
      {/* La evidencia tiene fecha, y la decisión se toma sobre ella. */}
      <Typography variant="caption" color="text.secondary">
        {t('network.review.cap.assembled', {
          date: formatDate(row.submittedAt, { dateStyle: 'long' }),
        })}
        {row.assembledReportId ? ` · ${t('network.review.cap.audit')}` : ''}
      </Typography>

      {granted && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {t('network.review.cap.granted')}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        variant="outlined"
        size="small"
        startIcon={<PictureAsPdfRounded />}
        disabled={busy}
        onClick={() => void printDossier()}
        sx={{ mt: 1.5 }}
      >
        {tCap('dossier.pdf.cta')}
      </Button>

      {/* LA PORTADA ANTES QUE LA EVIDENCIA: qué tengo delante y qué mirar.
          Debajo sigue el expediente completo, en el orden que enseña el método. */}
      <CapDossierCover cover={buildCapDossierCover(row.packet)} />

      {/* EL ORDEN DE LAS PESTAÑAS ES EL ORDEN DE LECTURA, y eso no es estética:
          la página larga que había antes enseñaba el método —lo que la malla MIDE
          antes de lo que el centro ESCRIBIÓ— y un revisor nuevo lo aprendía
          leyendo. Con pestañas eso se pierde salvo que se conserve el orden. */}
      <Tabs
        value={tab}
        onChange={(_, v: EvidenceTab) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="audit" label={tCap('dossier.tabs.audit')} />
        <Tab value="ccds" label={tCap('dossier.tabs.ccds')} />
        <Tab value="matrix" label={tCap('dossier.tabs.matrix')} />
        <Tab value="sections" label={tCap('dossier.tabs.sections')} />
        <Tab value="stated" label={tCap('dossier.tabs.stated')} />
      </Tabs>

      {(tab === 'audit' || tab === 'ccds' || tab === 'matrix') && (
        <CapEvidencePanel
          compliance={row.packet.compliance}
          ccdAnnex={row.packet.ccdAnnex}
          matrixAnnex={row.packet.matrixAnnex}
          level={row.level}
          only={tab}
        />
      )}

      {tab === 'sections' && (
        <Paper variant="outlined" sx={{ p: 2.5, mt: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap">
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('network.review.cap.sectionsTitle')}
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={t('network.inbox.cap.sections', { complete, total: sections.length })}
            />
            {hardGaps > 0 && (
              <Chip
                size="small"
                color="error"
                label={t('network.inbox.cap.gaps', { n: hardGaps })}
              />
            )}
            <Chip
              size="small"
              variant="outlined"
              label={t('network.review.cap.faculty', { n: row.packet.facultyCount })}
              sx={{ ml: 'auto' }}
            />
          </Stack>
          {/* Un tick verde dice que la sección está completa, no que el programa
            cumpla. Junto a "respaldada por una auditoría" se leía como lo
            segundo. */}
          <Typography variant="caption" color="text.secondary">
            {t('network.review.cap.sectionsMeaning')}
          </Typography>
          <Divider sx={{ mt: 1.5 }} />
          <Stack divider={<Divider />}>
            {sections.map((s) => (
              <Box key={s.key} sx={{ py: 1.5, display: 'flex', gap: 1.5 }}>
                {s.hardGap ? (
                  <ErrorOutlineRounded color="error" sx={{ fontSize: 20, mt: 0.25 }} />
                ) : s.complete ? (
                  <CheckCircleRounded color="success" sx={{ fontSize: 20, mt: 0.25 }} />
                ) : (
                  <RadioButtonUncheckedRounded sx={{ fontSize: 20, mt: 0.25, opacity: 0.4 }} />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600 }}>{SECTION_LABEL[s.key] ?? s.key}</Typography>
                  {/* El resumen automático y la narrativa del centro son cosas
                    distintas y se muestran distinto: el primero lo derivó la
                    plataforma, el segundo lo escribió una persona. */}
                  {s.autoSummary && (
                    <Typography variant="body2" color="text.secondary">
                      {s.autoSummary}
                    </Typography>
                  )}
                  {s.content && (
                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                      {s.content}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Atestaciones: lo que el centro afirmó al presentar. `submitting_with_known_gaps`
          es la que más pesa — dice si sabía que sometía incompleto. */}
      {tab === 'stated' && row.packet.attestations.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2.5, mt: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
            {t('network.review.cap.attestations')}
          </Typography>
          <Stack spacing={1}>
            {row.packet.attestations.map((a) => (
              <Stack key={a.key} direction="row" spacing={1} alignItems="center">
                {a.met ? (
                  <CheckCircleRounded color="success" sx={{ fontSize: 18 }} />
                ) : (
                  <RadioButtonUncheckedRounded sx={{ fontSize: 18, opacity: 0.4 }} />
                )}
                <Typography variant="body2">
                  {t(`network.review.cap.attestation.${a.key}`)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2.5, mt: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('network.review.cap.decisionTitle')}
        </Typography>
        {!open ? (
          <>
            <Alert severity="info" sx={{ mt: 1.5 }}>
              {t('network.review.cap.closed', {
                status: t(`network.inbox.status.${row.status}`, { defaultValue: row.status }),
              })}
            </Alert>
            {/* Lo que el revisor respondió, tal como lo escribió: una ronda cerrada
                que no muestra su respuesta obliga a recordarla. */}
            {row.decision?.observations && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  {t('network.review.cap.observations')}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                  {row.decision.observations}
                </Typography>
              </Box>
            )}
            {/* El análisis que el revisor TUVO DELANTE al decidir. Se guarda
                justamente para que dentro de seis meses alguien pueda ver sobre
                qué se resolvió; esconderlo al cerrar la ronda anulaba esa razón.
                Sin botón de generar ni de copiar: no hay dónde llevarlo. */}
            <CapAnalysisPanel
              analysis={analysis}
              currentVersion={row.packet.version}
              busy={false}
              onGenerate={() => {}}
              onUseDraft={() => {}}
              readOnly
            />
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('network.review.cap.decisionHelp')}
            </Typography>
            {busy && <LinearProgress sx={{ mb: 2 }} />}
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              label={t('network.review.cap.observations')}
              helperText={t('network.review.cap.observationsHelp')}
              disabled={busy}
              sx={{ mb: 2 }}
            />
            <CapAnalysisPanel
              analysis={analysis}
              currentVersion={row.packet.version}
              busy={analysing}
              onGenerate={generateAnalysis}
              onUseDraft={setObservations}
            />
            {row.submissionKind === 'advisory' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('network.review.cap.advisoryNote')}
              </Alert>
            )}
            {hardGaps > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('network.review.cap.gapsBlock', { n: hardGaps })}
              </Alert>
            )}
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                color="success"
                disabled={busy || !canApprove}
                onClick={() => void decide('approved')}
              >
                {t('network.review.cap.approve')}
              </Button>
              {/* Otorga igual que aprobar: el centro queda certificado y puede
                  operar. Lo que agrega es qué debe y para cuándo. */}
              <Button
                variant="outlined"
                color="success"
                disabled={busy || !canApprove}
                onClick={() => setConditionsOpen(true)}
              >
                {t('network.review.cap.approveWithConditions')}
              </Button>
              <Button
                variant="outlined"
                disabled={busy || observations.trim() === ''}
                onClick={() => void decide('returned')}
              >
                {t('network.review.cap.return')}
              </Button>
              <Button
                variant="outlined"
                color="error"
                disabled={busy || observations.trim() === ''}
                onClick={() => void decide('rejected')}
              >
                {t('network.review.cap.reject')}
              </Button>
            </Stack>
          </>
        )}
      </Paper>

      <ConditionalApprovalDialog
        open={conditionsOpen}
        cover={buildCapDossierCover(row.packet)}
        busy={busy}
        onCancel={() => setConditionsOpen(false)}
        onConfirm={(conditions) => void decide('approved_with_conditions', conditions)}
      />
    </Box>
  );
}
